import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClient, type MeResponse } from '../api/api.client';
import { AuthService } from '../auth/auth.service';

/**
 * Profil client utile hors de `/profil` — notamment la photo d'avatar.
 *
 * Le badge du header lit Cognito pour le nom, mais la photo vit en base via
 * `/me`. Ce service la charge une fois la session ouverte et la met à jour
 * quand le profil est enregistré, pour que la bulle suive sans rechargement.
 */
@Injectable({ providedIn: 'root' })
export class ClientMeService {
  private readonly api = inject(ApiClient);
  private readonly auth = inject(AuthService);

  private readonly photo = signal<string | null>(null);
  private refreshToken = 0;

  readonly photoUrl = this.photo.asReadonly();

  constructor() {
    effect(() => {
      const authenticated = this.auth.isAuthenticated();
      untracked(() => {
        if (!authenticated) {
          this.photo.set(null);
          return;
        }
        void this.refresh();
      });
    });
  }

  /** Applique un `/me` déjà chargé (évite un second aller-retour après save). */
  apply(me: Pick<MeResponse, 'photoUrl'>): void {
    this.photo.set(me.photoUrl ?? null);
  }

  async refresh(): Promise<void> {
    if (!this.auth.isAuthenticated()) {
      this.photo.set(null);
      return;
    }
    const token = ++this.refreshToken;
    try {
      const me = await firstValueFrom(this.api.me());
      if (token !== this.refreshToken) {
        return;
      }
      this.photo.set(me.photoUrl ?? null);
    } catch {
      if (token !== this.refreshToken) {
        return;
      }
      // On garde la dernière photo connue plutôt que de vider le badge sur un
      // échec réseau passager.
    }
  }

  clear(): void {
    this.refreshToken += 1;
    this.photo.set(null);
  }
}
